import api from './api';
import type { License, Installation, LicenseHistory, PaginatedResponse, Runtime, LicenseStatus } from '../types';

interface ListLicensesParams {
  page?: number;
  limit?: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  customerId?: string;
  productId?: string;
  status?: LicenseStatus;
  search?: string;
}

interface CreateLicenseInput {
  customerId: string;
  productId: string;
  deviceCount: number;
  runtime: Runtime;
  notes?: string;
  hardwareId?: string;
}

interface UpdateLicenseInput {
  deviceCount?: number;
  status?: LicenseStatus;
  notes?: string;
  hardwareId?: string;
}

export async function listLicenses(params: ListLicensesParams = {}): Promise<PaginatedResponse<License>> {
  const response = await api.get<PaginatedResponse<License>>('/licenses', { params });
  return response.data;
}

export async function getLicense(id: string): Promise<License> {
  const response = await api.get<License>(`/licenses/${id}`);
  return response.data;
}

export async function createLicense(data: CreateLicenseInput): Promise<License> {
  const response = await api.post<License>('/licenses', data);
  return response.data;
}

export async function updateLicense(id: string, data: UpdateLicenseInput): Promise<License> {
  const response = await api.put<License>(`/licenses/${id}`, data);
  return response.data;
}

export async function revokeLicense(id: string): Promise<License> {
  const response = await api.put<License>(`/licenses/${id}/revoke`);
  return response.data;
}

export async function extendLicense(id: string, runtime: Runtime): Promise<License> {
  const response = await api.put<License>(`/licenses/${id}/extend`, { runtime });
  return response.data;
}

export async function changeDeviceCount(id: string, deviceCount: number): Promise<License> {
  const response = await api.put<License>(`/licenses/${id}/devices`, { deviceCount });
  return response.data;
}

export async function getInstallations(licenseId: string): Promise<Installation[]> {
  const response = await api.get<Installation[]>(`/licenses/${licenseId}/installations`);
  return response.data;
}

export async function deactivateInstallation(licenseId: string, installationId: string): Promise<void> {
  await api.put(`/licenses/${licenseId}/installations/${installationId}/deactivate`);
}

export async function reactivateInstallation(licenseId: string, installationId: string): Promise<void> {
  await api.put(`/licenses/${licenseId}/installations/${installationId}/reactivate`);
}

export async function deleteInstallation(licenseId: string, installationId: string): Promise<void> {
  await api.delete(`/licenses/${licenseId}/installations/${installationId}`);
}

export async function downloadOfflineLicense(licenseId: string, licenseKey: string): Promise<void> {
  const response = await api.get(`/licenses/${licenseId}/offline-license`, {
    responseType: 'blob',
  });
  const url = window.URL.createObjectURL(response.data as Blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `wifi-tracker-${licenseKey}.lic`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
}

export async function getCustomerLicenses(customerId: string): Promise<License[]> {
  const response = await api.get<License[]>(`/licenses/customer/${customerId}`);
  return response.data;
}

export async function getLicenseHistory(licenseId: string): Promise<LicenseHistory[]> {
  const response = await api.get<LicenseHistory[]>(`/licenses/${licenseId}/history`);
  return response.data;
}
